import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, {
  ValueType,
  components,
  MultiValueProps,
  MenuListComponentProps
} from 'react-select';
import moment from 'moment';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import {
  LandlordDetailedReportSearchModel,
  LandlordDetailedReportTotalsModel,
  LandlordReportFilterOptionsModel,
  LandlordReportMode,
  LandlordReportNodeModel
} from '../shared/models/landlord-detailed-report.model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import {
  exportLandlordDetailedReport,
  getLandlordDetailedReport,
  getLandlordReportFilterOptions
} from './landlord-detailed-report.service';

const selectMenuPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

const EMPTY_TOTALS: LandlordDetailedReportTotalsModel = {
  totalRentGeneratedPerUnit: 0,
  amountDueToLandlord: 0,
  expenses: 0,
  llAmount: 0
};

const MODES: { value: LandlordReportMode; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'landlord', label: 'Landlord Aggregated' },
  { value: 'area', label: 'Area Aggregated' }
];

/** List mode keeps the full per-reservation detail; the aggregated modes are the money columns. */
const LIST_COLUMNS = 22;
const AGGREGATED_COLUMNS = 8;

/**
 * Only used when the account column is deliberately hidden — see the toggle above the filters.
 * Masking style matches the landlord bank-details card so the two read the same.
 */
const maskAccountNumber = (value: string): string => {
  if (!value) return '—';
  const last4 = value.slice(-4);
  const masked = '•'.repeat(Math.max(value.length - 4, 0)) + last4;
  return (masked.match(/.{1,4}/g) || [masked]).join(' ');
};

const EMPTY_OPTIONS: LandlordReportFilterOptionsModel = {
  landlords: [],
  areas: [],
  properties: [],
  unitTypes: [],
  unitTypeGroups: [],
  rooms: []
};

/**
 * Money columns are read by scanning down them, so the digits need a fixed-width grid. Gilroy's
 * proportional figures make a 1 narrower than an 8 and the decimal points stop lining up.
 */
const MONEY_CELL = 'text-right tabular-nums';

/**
 * Negative money. secondary-700 rather than the stock Tailwind rose this page used: rose is not in
 * the design system, and of the on-system reds only 700 clears AA on white at this size
 * (secondary-600 is 3.8:1, action.danger 4.4:1, secondary-700 5.8:1).
 */
const NEGATIVE_MONEY = 'text-secondary-700';

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (ms?: number) => {
  if (!ms) return '—';
  return moment(ms).format('DD MMM YYYY');
};

type StringOption = { value: string; label: string };
type NumberOption = { value: number; label: string };
type FilterOption = StringOption | NumberOption;

/** How many chips a filter shows before the rest collapse into a count. */
const MAX_CHIPS = 2;

/**
 * Six filters share a four-column grid, so a filter holding twenty landlords cannot render twenty
 * chips — the row would grow taller than the table below it.
 */
const CollapsedMultiValue = (props: MultiValueProps<FilterOption> & { index: number }) => {
  if (props.index < MAX_CHIPS) {
    return <components.MultiValue {...props} />;
  }
  if (props.index > MAX_CHIPS) {
    return null;
  }

  const selectedOptions = props.getValue();
  return (
    <span
      className="ml-1 self-center text-sm text-neutral-500"
      title={selectedOptions.map((option) => option.label).join(', ')}
    >
      +{selectedOptions.length - MAX_CHIPS} more
    </span>
  );
};

/**
 * react-select renders every matching option into the DOM, and these dropdowns are fed by the
 * whole estate — thousands of rooms. Capping what is mounted keeps opening a menu cheap; the
 * dropdowns are searchable, so narrowing by typing reaches anything past the cap.
 */
const MAX_RENDERED_OPTIONS = 200;

const CappedMenuList = (props: MenuListComponentProps<FilterOption, true>) => {
  const { children } = props;

  if (!Array.isArray(children) || children.length <= MAX_RENDERED_OPTIONS) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  return (
    <components.MenuList {...props}>
      {children.slice(0, MAX_RENDERED_OPTIONS)}
      <div className="px-3 py-2 text-sm text-neutral-400">
        Showing {MAX_RENDERED_OPTIONS} of {children.length} — type to narrow
      </div>
    </components.MenuList>
  );
};

// react-select types MultiValue without the `index` it passes at runtime.
const filterSelectComponents = {
  MultiValue: CollapsedMultiValue as any,
  MenuList: CappedMenuList as any
};

/**
 * The options currently selected, as react-select wants them. Memoised so the `value` prop keeps
 * its identity between renders instead of handing the Select a fresh array every time.
 */
const useSelectedOptions = <Value extends string | number, Option extends { value: Value }>(
  availableOptions: Option[],
  selectedValues: Value[]
) =>
  useMemo(() => {
    if (!selectedValues.length) return [];
    const wanted = new Set<Value>(selectedValues);
    return availableOptions.filter((option) => wanted.has(option.value));
  }, [availableOptions, selectedValues]);

/**
 * Every fetch on this page settles into setState, and the report is slow enough to still be in
 * flight when someone navigates away — which React reports as a leak, and which would also pop an
 * error toast over whatever page they moved to.
 */
const useIsMounted = () => {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
};

/**
 * Drop selections that the dropdown no longer offers, keeping the array identity when there is
 * nothing to drop so the effect does not re-fire on its own output.
 */
const usePruneToOptions = <Value extends string | number>(
  setSelectedValues: React.Dispatch<React.SetStateAction<Value[]>>,
  availableOptions: { value: Value }[]
) => {
  useEffect(() => {
    const available = new Set(availableOptions.map((option) => option.value));
    setSelectedValues((current) => {
      const next = current.filter((value) => available.has(value));
      return next.length === current.length ? current : next;
    });
  }, [availableOptions, setSelectedValues]);
};

type NodeRowProps = {
  node: LandlordReportNodeModel;
  depth: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  revealAccounts: boolean;
};

/** The three payout columns, rendered identically in both tables. */
type ResolvedBank =
  | { status: 'one'; account: NonNullable<LandlordReportNodeModel['bankAccount']> }
  | { status: 'multiple' }
  | { status: 'none' };

/**
 * The account to show on a row that is not itself a unit.
 *
 * Bank details hang off units, because a landlord *name* can cover many landlord records — 'Sahel'
 * is 206 of them — and there is no single account for such a group. But most landlords are not
 * Sahel: where every unit underneath a row resolves to the same account, that row can show it, and
 * finance can pay from the landlord line without drilling in. Where they differ, saying so is more
 * useful than a blank.
 *
 * The whole tree is in the payload regardless of what is expanded, so this is derived here rather
 * than adding a rolled-up field to the API.
 */
const resolveBankAccount = (node: LandlordReportNodeModel): ResolvedBank => {
  const accounts = new Map<string, NonNullable<LandlordReportNodeModel['bankAccount']>>();
  const walk = (current: LandlordReportNodeModel) => {
    if (current.bankAccount && current.bankAccount.accountNumber) {
      accounts.set(current.bankAccount.accountNumber, current.bankAccount);
    }
    (current.children || []).forEach(walk);
  };
  walk(node);

  const found = Array.from(accounts.values());
  if (found.length === 1) return { status: 'one', account: found[0] };
  if (found.length > 1) return { status: 'multiple' };
  return { status: 'none' };
};

const BankCells = ({
  node,
  revealAccounts
}: {
  node: LandlordReportNodeModel;
  revealAccounts: boolean;
}) => {
  const resolved = useMemo(() => resolveBankAccount(node), [node]);

  // A reservation or an expense is already under the unit that carries the account; repeating it
  // on every child would just be noise.
  if (node.kind === 'reservation' || node.kind === 'expense') {
    return (
      <>
        <td>—</td>
        <td>—</td>
        <td>—</td>
      </>
    );
  }

  if (resolved.status === 'multiple') {
    return (
      <>
        <td>—</td>
        <td>—</td>
        <td className="whitespace-nowrap text-neutral-400">Multiple accounts</td>
      </>
    );
  }

  if (resolved.status === 'none') {
    return (
      <>
        <td>—</td>
        <td>—</td>
        <td className="whitespace-nowrap text-neutral-400">No bank details</td>
      </>
    );
  }

  const bank = resolved.account;
  return (
    <>
      <td>{bank.bankName || '—'}</td>
      <td>{bank.accountHolderName || '—'}</td>
      <td className="whitespace-nowrap font-gilroy-regular">
        {revealAccounts ? bank.accountNumber : maskAccountNumber(bank.accountNumber)}
      </td>
    </>
  );
};

/** Chevron gutter. Leaves get a spacer so every row's first real column lines up. */
const Expander = ({
  node,
  depth,
  isOpen,
  onToggle
}: {
  node: LandlordReportNodeModel;
  depth: number;
  isOpen: boolean;
  onToggle: (key: string) => void;
}) => {
  const hasChildren = Boolean(node.children && node.children.length);
  return (
    <td className="border-t border-gray-200 px-2 py-3.5 align-middle whitespace-nowrap">
      <span style={{ paddingLeft: depth * 18 }} className="inline-flex items-center">
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
            onClick={() => onToggle(node.key)}
            className="mr-1 flex h-6 w-6 items-center justify-center rounded text-neutral-400 hover:bg-primary-50 hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
          >
            {isOpen ? '−' : '+'}
          </button>
        ) : (
          <span className="mr-1 inline-block h-6 w-6" />
        )}
      </span>
    </td>
  );
};

/**
 * Reservations and expenses hang off the same unit at the same depth, so indentation cannot tell
 * them apart and the row has to say which it is. Every other kind is already given away by how far
 * it is indented, where a label would only be decoration.
 */
const KIND_LABEL: Partial<Record<LandlordReportNodeModel['kind'], string>> = {
  reservation: 'Reservation',
  expense: 'Expense'
};

const KindLabel = ({ kind }: { kind: LandlordReportNodeModel['kind'] }) => {
  const label = KIND_LABEL[kind];
  if (!label) return null;
  return <span className="mr-2 text-base font-gilroy-medium text-neutral-300">{label}</span>;
};

/**
 * The aggregated tree: label, then the three money columns the payout workbook uses.
 * Recursive, and rendered only when its parent is open, which is what keeps a deep tree cheap.
 */
const AggregatedRows = ({ node, depth, expanded, onToggle, revealAccounts }: NodeRowProps) => {
  const isOpen = expanded.has(node.key);
  const isExpense = node.kind === 'expense';
  return (
    <>
      <tr
        className={`transition-colors hover:bg-creams-100 [&>td]:border-t [&>td]:border-gray-200 [&>td]:px-4 [&>td]:py-4 [&>td]:text-xl ${
          node.kind === 'area' || node.kind === 'landlord'
            ? '[&>td]:font-gilroy-medium [&>td]:text-neutral-500'
            : '[&>td]:font-gilroy-regular [&>td]:text-neutral-400'
        }`}
      >
        <Expander node={node} depth={depth} isOpen={isOpen} onToggle={onToggle} />
        <td>
          <KindLabel kind={node.kind} />
          {node.label}
          {isExpense && node.expense ? (
            <span className="ml-2 text-base text-neutral-300">
              {node.expense.category}
              <span className="ml-2">{formatDate(node.expense.paymentDate)}</span>
            </span>
          ) : null}
        </td>
        <td className={MONEY_CELL}>{formatMoney(node.amountDueToLandlord)}</td>
        <td className={`${MONEY_CELL} ${node.expenses ? NEGATIVE_MONEY : ''}`}>
          {node.expenses ? formatMoney(node.expenses) : '—'}
        </td>
        <td className={`${MONEY_CELL} font-gilroy-bold text-neutral-500`}>
          {formatMoney(node.llAmount)}
        </td>
        <BankCells node={node} revealAccounts={revealAccounts} />
      </tr>
      {isOpen && node.children
        ? node.children.map((child) => (
            <AggregatedRows
              key={child.key}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))
        : null}
    </>
  );
};

const AggregatedTable = ({
  nodes,
  totals,
  mode,
  expanded,
  onToggle,
  revealAccounts
}: {
  nodes: LandlordReportNodeModel[];
  totals: LandlordDetailedReportTotalsModel;
  mode: LandlordReportMode;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  revealAccounts: boolean;
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-[1500px] w-full border-separate border-spacing-0"
        aria-label={mode === 'area' ? 'Area aggregated report' : 'Landlord aggregated report'}
      >
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-gray-200 text-neutral-400 [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:text-base">
            <th className="px-2" scope="col" aria-label="Expand" />
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              {mode === 'area' ? 'Area / Landlord / Unit' : 'Landlord / Unit'}
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Amount Due to Landlord
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Expenses
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              LL Amount
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Bank
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Holder
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Number
            </th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => (
            <AggregatedRows
              key={node.key}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 bg-white">
          <tr className="[&>td]:border-t-2 [&>td]:border-gray-200 [&>td]:px-4 [&>td]:py-5 [&>td]:text-2xl [&>td]:font-gilroy-bold [&>td]:text-neutral-900">
            <td colSpan={AGGREGATED_COLUMNS - 6} className="text-left">
              Grand total
            </td>
            <td className={MONEY_CELL}>{formatMoney(totals.amountDueToLandlord)}</td>
            <td className={`${MONEY_CELL} ${NEGATIVE_MONEY}`}>{formatMoney(totals.expenses)}</td>
            <td className={MONEY_CELL}>{formatMoney(totals.llAmount)}</td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

/**
 * List mode: unit rows carrying the subtotal, expanding to their reservations and expense lines.
 * Grouped by unit rather than flat because expenses belong to a unit, and a unit can have several
 * reservations in one window — hanging them off each reservation would count them more than once.
 */
const ListRows = ({ node, depth, expanded, onToggle, revealAccounts }: NodeRowProps) => {
  const isOpen = expanded.has(node.key);
  const r = node.reservation;
  const isUnit = node.kind === 'unit';
  return (
    <>
      <tr
        className={`transition-colors hover:bg-creams-100 [&>td]:border-t [&>td]:border-gray-200 [&>td]:px-4 [&>td]:py-4 [&>td]:text-xl ${
          isUnit
            ? '[&>td]:font-gilroy-medium [&>td]:text-neutral-500'
            : '[&>td]:font-gilroy-regular [&>td]:text-neutral-400'
        }`}
      >
        <Expander node={node} depth={depth} isOpen={isOpen} onToggle={onToggle} />
        <td className="whitespace-nowrap">
          <KindLabel kind={node.kind} />
          {node.label}
        </td>
        <td>{r ? r.landlordId || '—' : node.landlord?.landlordId || '—'}</td>
        <td>{r ? r.landlordName || '—' : node.landlord?.landlordName || '—'}</td>
        <td>{r ? r.landlordPhone || '—' : node.landlord?.landlordPhone || '—'}</td>
        <td>{r ? r.landlordEmail || '—' : node.landlord?.landlordEmail || '—'}</td>
        <td>{r ? r.propertyName || '—' : node.unit?.propertyName || '—'}</td>
        <td>{r ? r.unitId : node.unit?.unitId ?? '—'}</td>
        <td>{r ? r.unitName || '—' : node.unit?.unitName || '—'}</td>
        <td>{r ? r.unitShortName || '—' : node.unit?.unitShortName || '—'}</td>
        <td>{r ? r.unitTypeGroupName || '—' : node.unit?.unitTypeGroupName || '—'}</td>
        <td>{r ? r.areaId || '—' : '—'}</td>
        <td>{r ? r.areaName || '—' : node.unit?.areaName || '—'}</td>
        <td className={MONEY_CELL}>{r ? r.bedrooms ?? '—' : node.unit?.bedrooms ?? '—'}</td>
        <td className={MONEY_CELL}>{formatMoney(node.totalRentGeneratedPerUnit)}</td>
        <td className={MONEY_CELL}>{formatMoney(node.amountDueToLandlord)}</td>
        <td className={`${MONEY_CELL} ${node.expenses ? NEGATIVE_MONEY : ''}`}>
          {node.expenses ? formatMoney(node.expenses) : '—'}
        </td>
        <td className={`${MONEY_CELL} font-gilroy-bold text-neutral-500`}>
          {formatMoney(node.llAmount)}
        </td>
        <td className="whitespace-nowrap">
          {node.kind === 'expense' && node.expense
            ? `${node.expense.category}, ${formatDate(node.expense.paymentDate)}`
            : r
            ? `${formatDate(r.checkInDate)} to ${formatDate(r.checkOutDate)}`
            : '—'}
        </td>
        <BankCells node={node} revealAccounts={revealAccounts} />
      </tr>
      {isOpen && node.children
        ? node.children.map((child) => (
            <ListRows
              key={child.key}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))
        : null}
    </>
  );
};

const ListTable = ({
  nodes,
  totals,
  count,
  expanded,
  onToggle,
  revealAccounts
}: {
  nodes: LandlordReportNodeModel[];
  totals: LandlordDetailedReportTotalsModel;
  count: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  revealAccounts: boolean;
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-[3200px] w-full border-separate border-spacing-0"
        aria-label="Landlord detailed report"
      >
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-gray-200 text-neutral-400 [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:text-base">
            <th className="px-2" scope="col" aria-label="Expand" />
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit / Row
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord ID
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord Phone
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord Email
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Property Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit ID
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit Short Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit Type Group
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Area
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Area Name
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Bedrooms
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Total Rent Generated
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Amount Due to Landlord
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Expenses
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              LL Amount
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Dates
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Bank
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Holder
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Number
            </th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => (
            <ListRows
              key={node.key}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))}
        </tbody>
        {/* Totals span the whole filtered set, not this page, so paging never changes them. */}
        <tfoot className="sticky bottom-0 bg-white">
          <tr className="[&>td]:border-t-2 [&>td]:border-gray-200 [&>td]:px-4 [&>td]:py-5 [&>td]:text-2xl [&>td]:font-gilroy-bold [&>td]:text-neutral-900">
            <td colSpan={LIST_COLUMNS - 8} className="text-left">
              Totals — all {count} units
            </td>
            <td className={MONEY_CELL}>{formatMoney(totals.totalRentGeneratedPerUnit)}</td>
            <td className={MONEY_CELL}>{formatMoney(totals.amountDueToLandlord)}</td>
            <td className={`${MONEY_CELL} ${NEGATIVE_MONEY}`}>{formatMoney(totals.expenses)}</td>
            <td className={MONEY_CELL}>{formatMoney(totals.llAmount)}</td>
            <td colSpan={4} />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

const LandlordDetailedReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rows, setRows] = useState<LandlordReportNodeModel[]>([]);
  const [mode, setMode] = useState<LandlordReportMode>('list');
  // One flat set of node keys across every level — the keys are path-derived so they cannot collide.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Shown in full by default. This report exists to get transfers raised, and an account number
  // you have to un-hide before you can copy it is friction on the one task the page is for. The
  // report is already gated to finance. The toggle stays as a way to hide the column when the
  // screen is being shared.
  const [revealAccounts, setRevealAccounts] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [listCount, setListCount] = useState(0);
  const [totals, setTotals] = useState<LandlordDetailedReportTotalsModel>(EMPTY_TOTALS);
  const [truncated, setTruncated] = useState(false);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const [checkInDate, setCheckInDate] = useState<Date>(moment().startOf('month').toDate());
  // The window's end date is an inclusive night, so a whole month is the 1st to the 31st.
  // The window reads like a stay — check-out is not a charged night — so a whole month runs
  // 1st to 1st.
  const [checkOutDate, setCheckOutDate] = useState<Date>(
    moment().add(1, 'month').startOf('month').toDate()
  );

  const [landlordKeys, setLandlordKeys] = useState<string[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [unitTypeIds, setUnitTypeIds] = useState<number[]>([]);
  const [unitTypeGroupIds, setUnitTypeGroupIds] = useState<string[]>([]);
  const [roomIds, setRoomIds] = useState<string[]>([]);

  const [options, setOptions] = useState<LandlordReportFilterOptionsModel>(EMPTY_OPTIONS);

  const isMounted = useIsMounted();

  useEffect(() => {
    getLandlordReportFilterOptions().then((response) => {
      if (!isMounted.current) return;
      if (!isErrorInfo(response)) {
        setOptions(response);
      } else {
        birdToast.error('Filters could not be loaded', 'Landlord detailed report');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The dropdowns cascade in the order they are displayed: each one narrows every dropdown after
  // it. Landlord is last and therefore the most constrained — by the time you reach it, it only
  // offers landlords who actually own something in the current selection.
  //
  // Each stage is the set of unit types still reachable, and every dropdown is derived from the
  // stage above it. Deriving from unit types (rather than from the flat lists) is what lets a
  // later filter constrain an earlier dimension.
  // Each stage hands the next one the same array when it has nothing to filter on, so an untouched
  // filter costs neither a pass over the estate nor a new identity for the memos downstream.
  const byArea = useMemo(() => {
    if (!areaIds.length) return options.unitTypes;
    const wanted = new Set(areaIds);
    return options.unitTypes.filter((unitType) => wanted.has(unitType.areaId));
  }, [areaIds, options.unitTypes]);

  const byProperty = useMemo(() => {
    if (!propertyIds.length) return byArea;
    const wanted = new Set(propertyIds);
    return byArea.filter((unitType) => wanted.has(unitType.propertyId));
  }, [byArea, propertyIds]);

  const byUnit = useMemo(() => {
    if (!unitTypeIds.length) return byProperty;
    const wanted = new Set(unitTypeIds);
    return byProperty.filter((unitType) => wanted.has(unitType.id));
  }, [byProperty, unitTypeIds]);

  const byGroup = useMemo(() => {
    if (!unitTypeGroupIds.length) return byUnit;
    const wanted = new Set(unitTypeGroupIds);
    return byUnit.filter((unitType) => wanted.has(unitType.unitTypeGroupId));
  }, [byUnit, unitTypeGroupIds]);

  const byRoom = useMemo(() => {
    if (!roomIds.length) return byGroup;
    const wanted = new Set(roomIds);
    const owners = new Set<number>();
    options.rooms.forEach((room) => {
      if (wanted.has(room.roomId)) owners.add(room.unitTypeId);
    });
    return owners.size ? byGroup.filter((unitType) => owners.has(unitType.id)) : byGroup;
  }, [byGroup, options.rooms, roomIds]);

  // Name lookups, so each dropdown can label an id without carrying the name around.
  const areaNames = useMemo(
    () => new Map(options.areas.map((area) => [area.id, area.name])),
    [options.areas]
  );
  const propertyNames = useMemo(
    () => new Map(options.properties.map((property) => [property.id, property.name])),
    [options.properties]
  );
  const groupNames = useMemo(
    () => new Map(options.unitTypeGroups.map((group) => [group.id, group.name])),
    [options.unitTypeGroups]
  );
  // options.landlords is keyed by case-folded name, not landlordId — one name covers many landlord
  // records, and keying by id would list 200+ entries all reading the same name.
  const landlordNames = useMemo(
    () => new Map(options.landlords.map((landlord) => [landlord.id, landlord.name])),
    [options.landlords]
  );

  const distinct = (
    unitTypes: LandlordReportFilterOptionsModel['unitTypes'],
    key: 'areaId' | 'propertyId' | 'unitTypeGroupId' | 'landlordKey',
    names: Map<string, string>
  ): StringOption[] => {
    const seen = new Map<string, string>();
    unitTypes.forEach((unitType) => {
      const id = unitType[key];
      if (id) seen.set(id, names.get(id) || id);
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  };

  const areaOptions: StringOption[] = useMemo(
    () => distinct(options.unitTypes, 'areaId', areaNames),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areaNames, options.unitTypes]
  );

  const propertyOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byArea, 'propertyId', propertyNames),
    [byArea, propertyNames]
  );

  const unitTypeOptions: NumberOption[] = useMemo(() => {
    const seen = new Map<number, string>();
    byProperty.forEach((unitType) => seen.set(unitType.id, unitType.name || String(unitType.id)));
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  }, [byProperty]);

  const unitTypeGroupOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byUnit, 'unitTypeGroupId', groupNames),
    [byUnit, groupNames]
  );

  const roomOptions: StringOption[] = useMemo(() => {
    const reachable = new Set(byGroup.map((unitType) => unitType.id));
    return options.rooms
      .filter((room) => reachable.has(room.unitTypeId))
      .map((room) => ({ value: room.roomId, label: room.shortName }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [byGroup, options.rooms]);

  // Last in the chain, so it sees every other selection.
  const landlordOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byRoom, 'landlordKey', landlordNames),
    [byRoom, landlordNames]
  );

  // Narrowing an earlier filter can strand later selections that are no longer offered. Dropping
  // only the stranded ones here, rather than clearing the whole dropdown in each onChange, means
  // adding a second area keeps a property selection that is still reachable — and the rule holds
  // however the selection was reached, including the filter-options response arriving after a
  // choice was made.
  //
  // Returning the existing array untouched when nothing was stranded matters: a fresh array on
  // every render would re-trigger the effect forever.
  usePruneToOptions(setPropertyIds, propertyOptions);
  usePruneToOptions(setUnitTypeIds, unitTypeOptions);
  usePruneToOptions(setUnitTypeGroupIds, unitTypeGroupOptions);
  usePruneToOptions(setRoomIds, roomOptions);
  usePruneToOptions(setLandlordKeys, landlordOptions);

  const selectedAreas = useSelectedOptions(areaOptions, areaIds);
  const selectedProperties = useSelectedOptions(propertyOptions, propertyIds);
  const selectedUnitTypes = useSelectedOptions(unitTypeOptions, unitTypeIds);
  const selectedUnitTypeGroups = useSelectedOptions(unitTypeGroupOptions, unitTypeGroupIds);
  const selectedRooms = useSelectedOptions(roomOptions, roomIds);
  const selectedLandlords = useSelectedOptions(landlordOptions, landlordKeys);

  // Export and Generate have to ask for the same thing, or the sheet quietly disagrees with the
  // screen, so the request body is built once.
  const buildSearch = useCallback(
    (reportMode: LandlordReportMode, page: number, perPage: number) => ({
      checkInDate: moment(checkInDate).startOf('day').valueOf(),
      checkOutDate: moment(checkOutDate).startOf('day').valueOf(),
      offset: page,
      limit: perPage,
      mode: reportMode,
      query: {
        ...(landlordKeys.length ? { landlordNames: landlordKeys } : {}),
        ...(areaIds.length ? { areaIds } : {}),
        ...(propertyIds.length ? { propertyIds } : {}),
        ...(unitTypeIds.length ? { unitTypeIds } : {}),
        ...(unitTypeGroupIds.length ? { unitTypeGroupIds } : {}),
        ...(roomIds.length ? { roomIds } : {})
      }
    }),
    [
      areaIds,
      checkInDate,
      checkOutDate,
      landlordKeys,
      propertyIds,
      roomIds,
      unitTypeGroupIds,
      unitTypeIds
    ]
  );

  const handleExport = () => {
    if (!hasSearched) {
      birdToast.error('Generate the report before exporting it', 'Landlord detailed report');
      return;
    }
    setExporting(true);
    exportLandlordDetailedReport(buildSearch(mode, 1, limit)).then((response) => {
      if (!isMounted.current) return;
      setExporting(false);
      if (isErrorInfo(response)) {
        birdToast.error('The export could not be generated', 'Landlord detailed report');
      }
    });
  };

  const loadReportWithMode = useCallback(
    (reportMode: LandlordReportMode, page: number, perPage: number) => {
      setLoading(true);
      setError(false);

      const search: LandlordDetailedReportSearchModel = buildSearch(reportMode, page, perPage);

      getLandlordDetailedReport(search).then((response) => {
        if (!isMounted.current) return;
        setLoading(false);
        setHasSearched(true);

        if (isErrorInfo(response)) {
          setError(true);
          setRows([]);
          setListCount(0);
          setTotals(EMPTY_TOTALS);
          setTruncated(false);
          return;
        }

        setRows(response.rows);
        setListCount(response.count);
        setTotals(response.totals);
        setTruncated(response.truncated);
      });
    },
    [buildSearch, isMounted]
  );

  const loadReport = useCallback(
    (page: number, perPage: number) => loadReportWithMode(mode, page, perPage),
    [loadReportWithMode, mode]
  );

  // Stated in the UI rather than left to be inferred from the numbers: which nights this window
  // actually counts. The convention has been misread more than once, and every time it was because
  // nothing on screen said what the dates meant.
  const windowSummary = useMemo(() => {
    const from = moment(checkInDate).startOf('day');
    const to = moment(checkOutDate).startOf('day');
    const nights = to.diff(from, 'days');
    if (nights <= 0) {
      return null;
    }
    return {
      nights,
      firstNight: from.format('D MMM'),
      lastNight: to.clone().subtract(1, 'day').format('D MMM YYYY')
    };
  }, [checkInDate, checkOutDate]);

  const activeFilterCount = [
    areaIds,
    propertyIds,
    unitTypeIds,
    unitTypeGroupIds,
    roomIds,
    landlordKeys
  ].filter((values) => values.length > 0).length;

  const filterSummary =
    activeFilterCount === 0
      ? 'No filters'
      : `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}`;

  const handleGenerate = () => {
    if (!checkInDate || !checkOutDate) {
      birdToast.error('Pick both a check-in and a check-out date', 'Landlord detailed report');
      return;
    }

    // Both window dates are inclusive, so an equal pair is a legitimate single-night window.
    // Check-out is not a charged night, so an equal pair is an empty window that would report
    // nothing rather than erroring.
    if (!moment(checkOutDate).isAfter(moment(checkInDate), 'day')) {
      birdToast.error(
        'Check-out must be after check-in — the check-out day itself is not charged',
        'Landlord detailed report'
      );
      return;
    }

    setOffset(1);
    loadReport(1, limit);
  };

  const handleClearFilters = () => {
    setLandlordKeys([]);
    setAreaIds([]);
    setPropertyIds([]);
    setUnitTypeIds([]);
    setUnitTypeGroupIds([]);
    setRoomIds([]);
  };

  const toggleNode = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleModeChange = (next: LandlordReportMode) => {
    if (next === mode) {
      return;
    }
    setMode(next);
    // Expanded keys are mode-specific, and the shapes differ, so a stale set would leave rows open
    // that no longer exist. The report refetches because the server does the grouping.
    setExpanded(new Set());
    setOffset(1);
    if (hasSearched) {
      loadReportWithMode(next, 1, limit);
    }
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadReport(newPage, limit);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadReport(1, itemsPerPage);
  };

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      {/* Only the action bar is pinned. The filters stay on the page and scroll away with it, so a
          generated report is not permanently sharing the viewport with eight dropdowns. */}
      <section className="sticky top-0 z-30 -mx-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-gray-200 bg-creams-50 px-6 py-3">
        <h1 className="text-3xl font-recoleta font-semibold text-neutral-500">
          Landlord Detailed Report
        </h1>

        {/* Three buttons rather than the shared tabs component: that one owns its own active state
            and renders the panel body, both of which fight one dataset shown three ways. Classes are
            lifted from it so this still reads as the same design system. */}
        <div role="tablist" aria-label="Report view" className="flex flex-wrap gap-1">
          {MODES.map((option) => {
            const isActive = option.value === mode;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleModeChange(option.value)}
                className={`rounded-xl px-4 py-2 text-base font-gilroy-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-1 ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-elevation-1'
                    : 'text-primary-700 hover:bg-primary-50'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!hasSearched || exporting}
            onClick={handleExport}
            className="rounded-xl px-4 py-2 text-base font-gilroy-medium text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-1"
          >
            {exporting ? 'Preparing…' : 'Export to Excel'}
          </button>
          <button
            type="button"
            aria-pressed={revealAccounts}
            onClick={() => setRevealAccounts((current) => !current)}
            className="rounded-xl px-4 py-2 text-base font-gilroy-medium text-primary-700 transition-colors hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-1"
          >
            {revealAccounts ? 'Hide account numbers' : 'Show account numbers'}
          </button>
        </div>
      </section>

      <section className="mt-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">Area</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={filterSelectComponents}
              options={areaOptions}
              placeholder="All areas"
              value={selectedAreas}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) =>
                setAreaIds((value || []).map((option) => option.value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">
              Property
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={filterSelectComponents}
              options={propertyOptions}
              placeholder="All properties"
              value={selectedProperties}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) =>
                setPropertyIds((value || []).map((option) => option.value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">Unit</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={filterSelectComponents}
              options={unitTypeOptions}
              placeholder="All units"
              value={selectedUnitTypes}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<NumberOption, true>) =>
                setUnitTypeIds((value || []).map((option) => option.value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">
              Unit Type Group
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={filterSelectComponents}
              options={unitTypeGroupOptions}
              placeholder="All unit type groups"
              value={selectedUnitTypeGroups}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) =>
                setUnitTypeGroupIds((value || []).map((option) => option.value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">
              Unit Short Name
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={filterSelectComponents}
              options={roomOptions}
              placeholder="All short names"
              value={selectedRooms}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) =>
                setRoomIds((value || []).map((option) => option.value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">
              Landlord
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={filterSelectComponents}
              options={landlordOptions}
              placeholder={
                landlordOptions.length ? 'All landlords' : 'No landlords match the filters'
              }
              value={selectedLandlords}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) =>
                setLandlordKeys((value || []).map((option) => option.value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">
              Check-in Date
            </label>
            <ReactDatePicker
              selected={checkInDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setCheckInDate(date);
                if (checkOutDate.getTime() < date.getTime()) setCheckOutDate(date);
              }}
              placeholderText="From"
              className="h-[42px] w-full rounded border border-gray-200 px-3 text-base text-neutral-500 outline-none transition-colors focus:border-primary-600"
            />
          </div>

          <div>
            <label className="mb-1 block text-base font-gilroy-medium text-neutral-400">
              Check-out Date
            </label>
            <ReactDatePicker
              selected={checkOutDate}
              minDate={checkInDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setCheckOutDate(date);
              }}
              placeholderText="To"
              className="h-[42px] w-full rounded border border-gray-200 px-3 text-base text-neutral-500 outline-none transition-colors focus:border-primary-600"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <p
            className={`text-base font-gilroy-medium ${
              windowSummary ? 'text-neutral-400' : NEGATIVE_MONEY
            }`}
          >
            {windowSummary
              ? `${filterSummary}, counting ${windowSummary.nights} ${
                  windowSummary.nights === 1 ? 'night' : 'nights'
                } from ${windowSummary.firstNight} to ${windowSummary.lastNight}`
              : 'Check-out must be after check-in — the check-out day itself is not charged'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleGenerate}
              type={ButtonType.primary}
              size={ButtonSize.small}
              classNameButton="px-5 py-2.5 text-base leading-5"
            >
              Generate
            </Button>
            <Button
              onClick={handleClearFilters}
              type={ButtonType.primaryInverted}
              size={ButtonSize.small}
              disabled={activeFilterCount === 0}
              classNameButton="px-5 py-2.5 text-base leading-5"
            >
              Clear filters
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-4 space-y-4">
        {truncated && (
          <div className="rounded-2xl border border-secondary-200 bg-secondary-50 px-5 py-4">
            <p className="text-lg font-gilroy-medium text-secondary-800">
              This window returned more reservations than the report can total accurately. Narrow
              the date range or add a filter — the figures below are incomplete.
            </p>
          </div>
        )}

        {loading ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <LoadingData />
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-3xl font-recoleta text-neutral-500">
              The report could not be generated
            </h2>
            <p className="mt-3 max-w-xl text-lg font-gilroy-medium text-neutral-400">
              Retry, or narrow the date range if the window is very wide.
            </p>
            <Button
              onClick={() => loadReport(offset, limit)}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        ) : !hasSearched ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <h2 className="text-3xl font-recoleta text-neutral-500">
              Pick a date window, then Generate
            </h2>
            <p className="mt-3 text-lg font-gilroy-medium text-neutral-400">
              Rent is pro-rated across the nights you choose.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
            <NotFoundData />
          </div>
        ) : (
          <>
            {mode === 'list' ? (
              <ListTable
                nodes={rows}
                totals={totals}
                count={listCount}
                expanded={expanded}
                onToggle={toggleNode}
                revealAccounts={revealAccounts}
              />
            ) : (
              <AggregatedTable
                nodes={rows}
                totals={totals}
                mode={mode}
                expanded={expanded}
                onToggle={toggleNode}
                revealAccounts={revealAccounts}
              />
            )}

            {/* Paging runs over top-level nodes. The aggregated modes are tens of rows and come
                back whole, so a pager there would only be able to split a landlord from its units. */}
            {mode === 'list' && listCount > 0 && (
              <div className="flex items-center justify-center pb-4">
                <Pagination
                  itemCounts={listCount}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default LandlordDetailedReportPage;
